import { useState, type MouseEvent, type JSX } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import ListItemText from '@mui/material/ListItemText'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import BookmarkBorderOutlinedIcon from '@mui/icons-material/BookmarkBorderOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { MAX_SAVED_VIEWS, type SavedView } from '@profiler/shared'
import { useApi } from '../auth'
import { qk, useSavedViews } from '../queries'
import { ErrorPanel } from './States'

/**
 * Filter sets somebody named and kept.
 *
 * Every list here already puts its filters in the address bar, which makes a
 * view shareable — but only if you still have the link. The view people return
 * to weekly ("founders in India we can mail, not yet contacted") had to be
 * rebuilt from six dropdowns every time, and nobody rebuilds it exactly.
 *
 * What is stored is the QUERY STRING, not a parsed filter object. There is then
 * no second representation of a filter set to drift from the one the screen
 * actually reads, and a saved view keeps working the day a filter is added.
 *
 * Per person, on the server rather than in this browser: somebody who saves a
 * view on their laptop expects to find it on their desktop, and localStorage
 * quietly fails that.
 */
/** Why saving is unavailable, or the invitation to save. */
function saveHint(alreadySavedAs: string | null, mine: number): string {
  if (alreadySavedAs) return `Already saved as “${alreadySavedAs}”`
  if (mine >= MAX_SAVED_VIEWS) return `${MAX_SAVED_VIEWS} is the limit — delete one first`
  return 'Save this view…'
}

export function SavedViews({ screen }: Readonly<{ screen: SavedView['screen'] }>): JSX.Element {
  const api = useApi()
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()
  const saved = useSavedViews()
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)
  const [naming, setNaming] = useState(false)
  const [name, setName] = useState('')

  const mine = (saved.data ?? []).filter((v) => v.screen === screen)
  const current = location.search.replace(/^\?/, '')
  // The view you are already looking at, so saving it twice is not offered.
  const alreadySaved = mine.find((v) => v.query === current)

  const replace = useMutation({
    mutationFn: (views: SavedView[]) => api.putSavedViews(views),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: qk.savedViews })
    },
  })

  const open = (event: MouseEvent<HTMLElement>): void => setAnchor(event.currentTarget)
  const close = (): void => setAnchor(null)

  const save = (): void => {
    const trimmed = name.trim()
    if (!trimmed) return
    const others = (saved.data ?? []).filter((v) => !(v.screen === screen && v.name === trimmed))
    replace.mutate([
      ...others,
      {
        id: `${screen}-${Date.now().toString(36)}`,
        name: trimmed,
        screen,
        query: current,
        createdAt: new Date().toISOString(),
      },
    ])
    setNaming(false)
    setName('')
  }

  return (
    <>
      <Tooltip title="Filter sets you have saved">
        <Button size="small" color="inherit" startIcon={<BookmarkBorderOutlinedIcon />} onClick={open}>
          Views{mine.length > 0 ? ` (${mine.length})` : ''}
        </Button>
      </Tooltip>

      <Menu anchorEl={anchor} open={anchor !== null} onClose={close}>
        {mine.length === 0 ? (
          <Box sx={{ px: 2, py: 1.5, maxWidth: 280 }}>
            <Typography variant="body2" color="text.secondary">
              No saved views yet. Set the filters you want, then save them here to come back to exactly this
              list.
            </Typography>
          </Box>
        ) : (
          mine.map((view) => (
            <MenuItem
              key={view.id}
              selected={view.query === current}
              onClick={() => {
                close()
                // The query string IS the view: applying one is navigating to it,
                // so Back works and the link is still the link.
                navigate({ pathname: location.pathname, search: view.query })
              }}
              sx={{ pr: 1 }}
            >
              <ListItemText
                primary={view.name}
                secondary={view.query === '' ? 'No filters' : `${view.query.split('&').length} filters`}
                slotProps={{ secondary: { variant: 'caption' } }}
              />
              <Tooltip title="Delete this view">
                <IconButton
                  size="small"
                  edge="end"
                  aria-label={`Delete ${view.name}`}
                  disabled={replace.isPending}
                  onClick={(event) => {
                    // Without this the click also applies the view it is deleting.
                    event.stopPropagation()
                    replace.mutate((saved.data ?? []).filter((v) => v.id !== view.id))
                  }}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </MenuItem>
          ))
        )}

        <Divider />
        <MenuItem
          disabled={mine.length >= MAX_SAVED_VIEWS || alreadySaved !== undefined}
          onClick={() => {
            close()
            setNaming(true)
          }}
        >
          {saveHint(alreadySaved?.name ?? null, mine.length)}
        </MenuItem>
      </Menu>

      <Dialog open={naming} onClose={() => setNaming(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Save this view</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            The filters currently set are stored under a name. Saving over a name you already used replaces
            it.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="Name"
            placeholder="Founders in India we can mail"
            value={name}
            onChange={(event) => setName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') save()
            }}
            slotProps={{ htmlInput: { maxLength: 60 } }}
          />
          {replace.isError ? <ErrorPanel error={replace.error} /> : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNaming(false)}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={!name.trim() || replace.isPending}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
